/**
 * Data Kebijakan Privasi Aplikasi Presensi & Operasional
 * PT Kalisha Utama Ghani
 */

export const PRIVACY_POLICY_VERSION = '1.1';
export const PRIVACY_POLICY_STORAGE_KEY = 'privacy_policy_consent';

export const hasAgreedPrivacyPolicy = () => {
  try {
    const raw = localStorage.getItem(PRIVACY_POLICY_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed && parsed.version === PRIVACY_POLICY_VERSION && parsed.agreed === true;
  } catch (e) {
    return false;
  }
};

export const savePrivacyPolicyConsent = (nik = '') => {
  try {
    const data = {
      version: PRIVACY_POLICY_VERSION,
      agreed: true,
      agreed_at: new Date().toISOString(),
      nik: nik || null,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    };
    localStorage.setItem(PRIVACY_POLICY_STORAGE_KEY, JSON.stringify(data));
    return true;
  } catch (e) {
    console.error('Gagal menyimpan persetujuan kebijakan privasi:', e);
    return false;
  }
};

export const privacyHighlights = [
  {
    icon: 'MapPin',
    color: '#0284c7',
    bgColor: '#e0f2fe',
    title: 'Lokasi & Geofencing Presensi',
    desc: 'Mencocokkan koordinat GPS saat validasi perangkat dan presensi agar sesuai dengan radius resmi kantor kerja.',
  },
  {
    icon: 'Clock',
    color: '#0ea5e9',
    bgColor: '#f0f9ff',
    title: 'Monitoring Pasca Absen Masuk',
    desc: 'Memantau keberadaan di kantor pasca absen masuk (30 menit untuk delivery, 1 jam untuk staf kantor) dalam radius 180 m.',
  },
  {
    icon: 'Camera',
    color: '#16a34a',
    bgColor: '#dcfce7',
    title: 'Kamera & Pemindaian Visual',
    desc: 'Untuk swafoto (selfie) bukti kehadiran kerja serta scan barcode/QR serah terima, checklist, dan patroli.',
  },
  {
    icon: 'ShieldCheck',
    color: '#d97706',
    bgColor: '#fef3c7',
    title: 'Identifikasi Perangkat (UUID)',
    desc: 'Menghubungkan akun ke perangkat resmi terdaftar untuk mencegah kecurangan absen multi-device.',
  },
  {
    icon: 'Database',
    color: '#7c3aed',
    bgColor: '#f3e8ff',
    title: 'Penyimpanan Sesi & Offline',
    desc: 'Menyimpan riwayat dan antrean draft offline saat jaringan lemah agar data presensi tetap tersimpan aman.',
  },
];

export const privacySections = [
  {
    title: '1. Pendahuluan & Prinsip Perlindungan',
    content:
      'Aplikasi Presensi & Operasional PT Kalisha Utama Ghani berkomitmen melindungi data pribadi Anda sesuai Undang-Undang No. 27 Tahun 2022 tentang Pelindungan Data Pribadi (UU PDP). Kebijakan ini menjelaskan data yang dikumpulkan, dasar penggunaannya, serta jaminan kerahasiaan selama Anda menggunakan layanan aplikasi internal ini.',
  },
  {
    title: '2. Data Pribadi yang Kami Kumpulkan',
    content:
      'Untuk memastikan integritas dan operasional kehadiran kerja, aplikasi memproses:',
    bullets: [
      'Data Identitas: NIK (CXP...), Kode Debitur Agen, Nama Lengkap, Jabatan, Divisi, dan Kontak (Email/No. HP).',
      'Data Lokasi Geografis Real-Time: Koordinat GPS (latitude, longitude) dan estimasi jarak radius ke kantor operasional saat absensi atau dinas.',
      'Data Monitoring Keberadaan (Stay-on-Site): Pemantauan berkala lokasi setelah absen masuk untuk mencatat status kepatuhan tetap berada di lingkungan kantor.',
      'Data Gambar & Media: Foto swafoto presensi kehadiran, foto bukti surat izin/sakit/dinas, dan pemindaian QR/barcode serah terima.',
      'Data Perangkat: Unique Device Identifier (UUID) yang digenerate oleh peramban untuk mencegah satu akun dipindahtangankan ke perangkat lain secara ilegal.',
    ],
  },
  {
    title: '3. Kebijakan Monitoring Lokasi Pasca Absen Masuk (Stay-on-Site Policy)',
    content:
      'Untuk mencegah praktik absensi fiktif (seperti datang absen lalu meninggalkan tempat tugas tanpa izin), sistem menerapkan aturan pemantauan lokasi otomatis setelah tombol "Absen Masuk" berhasil ditekan:',
    bullets: [
      'Divisi Delivery: Lokasi dipantau berkala selama 30 menit setelah absen masuk sebelum petugas berangkat memulai pengantaran barang.',
      'Divisi Operasional Umum / Staf Kantor: Lokasi dipantau berkala selama 1 jam (60 menit) setelah absen masuk untuk memastikan kehadiran di kantor.',
      'Batas Radius Toleransi: Sistem menetapkan batas jarak maksimal 180 meter dari koordinat kantor yang divalidasi.',
      'Pengecualian: Divisi Pick-up atau operasional khusus tertentu yang telah disetujui dibebaskan dari monitoring stay-on-site sesuai sifat tugas lapangannya.',
      'Penyimpanan Riwayat Monitoring: Catatan kepatuhan lokasi pasca absen tersimpan di perangkat lokal dan disinkronkan ke server operasional.',
    ],
  },
  {
    title: '4. Tujuan Penggunaan & Pemrosesan Data',
    content:
      'Data yang dikumpulkan digunakan semata-mata untuk:',
    bullets: [
      'Verifikasi keabsahan presensi kehadiran karyawan dan operasional harian kantor/agen.',
      'Pencegahan kecurangan absensi (anti-fraud) seperti lokasi palsu (mock GPS), titip absen, atau meninggalkan tugas tanpa izin.',
      'Pengelolaan alur operasional internal (pengajuan izin, dinas, kasbon, handover barang, checklist kendaraan, laporan security).',
      'Audit kehadiran oleh divisi Human Capital (HC) dan manajemen yang berwenang.',
    ],
  },
  {
    title: '5. Izin Akses Perangkat yang Diminta',
    content:
      'Peramban akan meminta izin akses resmi pada saat fitur digunakan:',
    bullets: [
      'Izin Lokasi (Geolocation API): Wajib aktif saat validasi awal, absensi masuk/pulang, dan selama durasi monitoring pasca absen masuk.',
      'Izin Kamera: Digunakan saat swafoto kehadiran atau pemindaian barcode modul operasional.',
      'Penyimpanan Lokal (localStorage / IndexedDB): Menyimpan sesi login terverifikasi dan antrean offline agar aplikasi tetap responsif saat sinyal lemah.',
    ],
  },
  {
    title: '6. Keamanan & Kerahasiaan Data',
    content:
      'Seluruh komunikasi data antara aplikasi dan server dilindungi dengan enkripsi SSL/TLS. PT Kalisha Utama Ghani tidak pernah memperjualbelikan, menyewakan, atau memberikan data pribadi Anda kepada pihak luar yang tidak terafiliasi dengan operasional resmi perusahaan.',
  },
  {
    title: '7. Hak Anda & Persetujuan (Consent)',
    content:
      'Anda berhak mengetahui data apa saja yang tercatat dan berhak meminta klarifikasi melalui bagian Human Capital / Support IT jika terdapat ketidaksesuaian data. Dengan menyetujui kebijakan ini, Anda memberikan persetujuan eksplisit bagi perusahaan untuk memproses data lokasi presensi, monitoring pasca absen (30 menit / 1 jam), swafoto wajah, dan identifikasi perangkat.',
  },
];
